import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../chrome/AppShell';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import {
    Chipbar,
    MenuItem,
    MenuLabel,
    MenuSep,
    Segmented,
    TCard,
    TEmpty,
    TFoot,
    Toolbar,
    TRow,
    TSkeleton,
} from '../components/table';
import { PageHeadActions } from '../components/nav';
import {
    DAY,
    STAGE,
    STAGE_ORDER,
    dateRangeLabel,
    dayOf,
    humanize,
    startOfToday,
} from '../lib/jobs';

// One row per job; activations without a job show as their own row.
// No New button: jobs come from the Attio sync.

interface JobRow {
    jobId: number | null;
    activationId?: number;
    name: string;
    activationCount: number;
    startAt?: string;
    endAt?: string;
    billingEntity?: string;
    boothType?: string;
    eventType?: string;
    statusCounts: Record<string, number>;
    crewCount: number;
    isJob: boolean;
}

type View = 'upcoming' | 'pending' | 'past' | 'all';

type Row = JobRow;

const isPending = (r: Row) =>
    (r.statusCounts.pending ?? 0) > 0 &&
    Object.keys(r.statusCounts).every((s) => s === 'pending' || s === 'lost');
const dominant = (r: Row) =>
    STAGE_ORDER.find((s) => r.statusCounts[s]) ?? 'pending';
const isPast = (r: Row) => {
    const end = dayOf(r.endAt ?? r.startAt);
    return end !== null && end < startOfToday();
};
const dateLabel = (r: Row) => dateRangeLabel(r.startAt, r.endAt);
const eventType = humanize;

export default function PipelineV3() {
    const navigate = useNavigate();
    const api = useAuthenticatedApi();
    const [rows, setRows] = useState<JobRow[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [view, setView] = useState<View>('upcoming');
    const [query, setQuery] = useState('');
    const [type, setType] = useState<string | null>(null);
    const [billing, setBilling] = useState<string | null>(null);

    const load = useCallback(() => {
        api.get('/production/jobs')
            .then((r: any) => {
                setRows(
                    (r.data ?? []).map((x: any) => ({
                        ...x,
                        jobId: x.jobId ? +x.jobId : null,
                        activationId: x.activationId ? +x.activationId : undefined,
                        activationCount: +(x.activationCount ?? 1),
                        crewCount: +(x.crewCount ?? 0),
                        statusCounts: x.statusCounts ?? {},
                    })),
                );
                setFailed(false);
            })
            .catch(() => setFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(load, [load]);

    const all = useMemo(() => rows ?? [], [rows]);
    const types = useMemo(
        () => Array.from(new Set(all.map((r) => r.eventType).filter(Boolean) as string[])).sort(),
        [all],
    );
    const billings = useMemo(
        () => Array.from(new Set(all.map((r) => r.billingEntity).filter(Boolean) as string[])).sort(),
        [all],
    );

    const q = query.trim().toLowerCase();
    const inView = useCallback(
        (r: JobRow) =>
            view === 'all' ||
            (view === 'pending' && isPending(r)) ||
            (view === 'past' && !isPending(r) && isPast(r)) ||
            (view === 'upcoming' && !isPending(r) && !isPast(r)),
        [view],
    );
    const visible = useMemo(() => {
        const list = all.filter(
            (r) =>
                (q
                    ? r.name.toLowerCase().includes(q) ||
                      (r.billingEntity ?? '').toLowerCase().includes(q)
                    : inView(r)) &&
                (!type || r.eventType === type) &&
                (!billing || r.billingEntity === billing),
        );
        const key = (r: JobRow) => dayOf(r.startAt) ?? Number.MAX_SAFE_INTEGER;
        return list.sort((a, b) =>
            view === 'past' || q ? key(b) - key(a) : key(a) - key(b),
        );
    }, [all, q, inView, type, billing, view]);

    /* upcoming splits into next 7 days, then later */
    const groups = useMemo(() => {
        if (view !== 'upcoming' || q) return [{ label: '', rows: visible }];
        const weekEnd = startOfToday() + 7 * DAY;
        const soon = visible.filter((r) => (dayOf(r.startAt) ?? Infinity) < weekEnd);
        const later = visible.filter((r) => !soon.includes(r));
        return [
            { label: 'Next 7 days', rows: soon },
            { label: 'Later', rows: later },
        ].filter((g) => g.rows.length > 0);
    }, [visible, view, q]);

    const counts = {
        upcoming: all.filter((r) => !isPending(r) && !isPast(r)).length,
        pending: all.filter(isPending).length,
    };
    const viewLabel = { upcoming: 'Upcoming', pending: 'Pending deals', past: 'Past', all: 'All' }[view];
    const filtersOn = (type ? 1 : 0) + (billing ? 1 : 0);

    const open = (r: JobRow) =>
        r.isJob && r.jobId
            ? `/pipeline/${r.jobId}`
            : r.activationId
              ? `/activations/${r.activationId}`
              : '/pipeline';

    const row = (r: JobRow, i: number) => {
        const stage = STAGE[dominant(r)] ?? { label: dominant(r) };
        const n = r.statusCounts[dominant(r)] ?? 0;
        const partial = r.activationCount > 1 && n < r.activationCount ? ` ${n}/${r.activationCount}` : '';
        const meta = [
            r.activationCount > 1 ? `${r.activationCount} activations` : eventType(r.eventType),
            r.crewCount ? `${r.crewCount} crew` : '',
            r.billingEntity ?? '',
        ]
            .filter(Boolean)
            .join(' · ');
        return (
            <TRow
                key={r.jobId ?? `a${r.activationId}`}
                index={i}
                name={r.name}
                badge={dominant(r) === 'lost' ? 'Lost' : undefined}
                meta={meta}
                metaDate={dateLabel(r)}
                date={dateLabel(r)}
                status={
                    <span style={{ color: stage.tone }}>
                        {stage.label}
                        {partial}
                    </span>
                }
                href={open(r)}
                onOpen={() => navigate(open(r))}
                menuItems={[]}
            />
        );
    };

    return (
        <AppShell>
            <PageHeadActions
                title="Pipeline"
                sub={
                    rows
                        ? `${counts.upcoming} upcoming · ${counts.pending} pending ${counts.pending === 1 ? 'deal' : 'deals'}`
                        : 'Jobs from Attio'
                }
                actions={
                    <a
                        className="tb-btn"
                        href="/pipeline/settings"
                        onClick={(e) => {
                            if (e.metaKey || e.ctrlKey) return;
                            e.preventDefault();
                            navigate('/pipeline/settings');
                        }}
                        style={{ textDecoration: 'none' }}
                    >
                        Settings
                    </a>
                }
            />
            <Toolbar
                searchValue={query}
                onSearch={setQuery}
                searchPlaceholder="Search jobs or clients"
                filterCount={filtersOn}
                inline={
                    !q && (
                        <Segmented
                            label="View"
                            options={[
                                { value: 'upcoming', label: 'Upcoming' },
                                { value: 'pending', label: 'Pending' },
                                { value: 'past', label: 'Past' },
                                { value: 'all', label: 'All' },
                            ]}
                            value={view}
                            onChange={setView}
                        />
                    )
                }
                menu={
                    types.length || billings.length
                        ? (close) => (
                              <>
                                  {types.length > 0 && <MenuLabel>Event type</MenuLabel>}
                                  {types.map((t) => (
                                      <MenuItem key={t} on={type === t} onClick={() => { setType(type === t ? null : t); close(); }}>
                                          {eventType(t)}
                                      </MenuItem>
                                  ))}
                                  {types.length > 0 && billings.length > 0 && <MenuSep />}
                                  {billings.length > 0 && <MenuLabel>Billing entity</MenuLabel>}
                                  {billings.map((b) => (
                                      <MenuItem key={b} on={billing === b} onClick={() => { setBilling(billing === b ? null : b); close(); }}>
                                          {b}
                                      </MenuItem>
                                  ))}
                              </>
                          )
                        : undefined
                }
            />
            <Chipbar
                chips={[
                    ...(type ? [{ label: 'Event type', value: eventType(type), onClear: () => setType(null) }] : []),
                    ...(billing ? [{ label: 'Billing', value: billing, onClear: () => setBilling(null) }] : []),
                ]}
            />

            {failed && (
                <TCard>
                    <TEmpty message="Couldn’t load the pipeline." actionLabel="Retry" onAction={load} />
                </TCard>
            )}
            {!failed && rows === null && (
                <TCard>
                    <TSkeleton rows={5} />
                </TCard>
            )}
            {!failed && rows !== null && visible.length === 0 && (
                <TCard>
                    <TEmpty
                        message={q ? `Nothing matches “${query}”.` : `No ${viewLabel.toLowerCase()} jobs.`}
                        actionLabel={!q && view !== 'all' ? 'Show all' : undefined}
                        onAction={() => setView('all')}
                    />
                </TCard>
            )}
            {!failed &&
                visible.length > 0 &&
                groups.map((g) => (
                    <React.Fragment key={g.label || 'all'}>
                        {g.label && <p className="s-lab pl-group">{g.label}</p>}
                        <TCard>{g.rows.map(row)}</TCard>
                    </React.Fragment>
                ))}
            {!failed && rows !== null && (
                <TFoot>
                    {q
                        ? `${visible.length} results · all jobs`
                        : `${visible.length} of ${all.length} · ${viewLabel}${filtersOn ? ` · ${filtersOn} filter${filtersOn > 1 ? 's' : ''}` : ''}`}
                    {' · '}Jobs come from Attio — won deals become activations.
                </TFoot>
            )}
        </AppShell>
    );
}
